import { isNil, isEmpty } from 'lodash';
import { WidgetContext } from '@/modules/ta/widget/models/context/WidgetContext';
import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import { DataSourceType } from '@/modules/core/app/constants/datasource.constants';
import { DrawOption } from '@/modules/ta/widget/widget.constants';
import { MARK_RENDERED_TIMEOUT } from '@/modules/core/export/export.constants';

export class DashboardWidgetContext extends WidgetContext {
  /**
   * @override
   *
   * @param {Widget} widget
   * @param {WidgetDataConfig} config
   */
  applyContext(widget, config, isLegacy = false) {
    config.params.widget_page_id = isLegacy
      ? getters.dashboardDrilldown.getDashboardId()
      : getters.dashboard?.getDashboard().id;
  }

  /**
   * @param {Widget} widget
   * @param drawOption
   */
  evaluateDrawOption(widget, drawOption) {
    const parentDrawOption = this.getDrawOption(drawOption, widget.layout_id);
    if (
      widget.metadata.draw_options[drawOption].overridden ||
      isNil(parentDrawOption) ||
      drawOption === DrawOption.SHOW_BACKGROUND
    ) {
      return widget.metadata.draw_options[drawOption];
    }

    return parentDrawOption;
  }

  /**
   *
   * @param drawOption
   * @param layoutId
   * @returns {*}
   */
  getDrawOption(drawOption, layoutId = null) {
    return getters.dashboardLayout.getDrawOptions(layoutId)?.[drawOption];
  }

  /**
   *
   * @param widget
   * @returns {array}
   */
  getChartPalette(widget) {
    if (!isEmpty(widget?.metadata.chart_palette) && !widget.isBigNumberWidget()) {
      return [...widget.metadata.chart_palette];
    }

    const { metadata } = getters.dashboard.getDashboard();

    return isEmpty(metadata.chart_palette) ? metadata.chart_palette : [...metadata.chart_palette];
  }

  getSelectedEntity() {
    const { metadata } = getters.dashboard.getDashboard();
    if (metadata.selected_entity?.type === DataSourceType.CLIENT && metadata.selected_entity?.id) {
      return { type: 'client_id', id: metadata.selected_entity.id };
    }
    if (
      metadata.selected_entity?.type === DataSourceType.CLIENT_GROUP &&
      metadata.selected_entity?.id
    ) {
      return { type: 'client_group_id', id: metadata.selected_entity.id };
    }
  }

  /**
   * @param {Widget} widget
   */
  setParentProperties(widget) {
    widget.layout_id = getters.dashboard.getCurrentLayoutId();
    // Defensive guard: ensure report_id is never carried over from a prior Report Studio
    // session. Without this, a stale ReportStudioWidgetContext (not cleared on RS page
    // destroy) can stamp report_id onto a new dashboard widget, moving it to Report Studio.
    widget.report_id = null;
  }

  /**
   * @param widgetId
   */
  setWidgetIdAsRendered(widgetId) {
    setTimeout(
      () => dispatches.dashboardExport.setWidgetAsRendered(widgetId),
      MARK_RENDERED_TIMEOUT
    );
  }

  /**
   * @param widgetId
   */
  removeMapFromRenderSlot(widgetId) {
    commits.dashboardExport.clearMapRenderSlot({ widgetId });
  }

  /**
   * @returns {boolean}
   */
  isDemoModeEnabled(isLegacy = false) {
    return isLegacy
      ? getters.dashboardDrilldown.getIsDemoMode()
      : getters.dashboard.getDashboard().isDemoModeEnabled();
  }

  /**
   *
   * @returns {*}
   */
  getMetadata() {
    return getters.dashboard.getMetadata();
  }
}
